import React, { useRef, useEffect, useCallback, useState } from 'react';
import mapboxgl, { Map, Popup } from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { Box, Slider, Typography } from '@mui/material';
import { useAppState } from 'src/state-management/state-provider';
import { mapBoxConfig } from 'src/mapbox-helpers/config';
import { fitMapToBoundaries } from 'src/mapbox-helpers/map-layer-utils';
import { addSiteBoundaryWithColor } from './map-helper'; // Removed addLocationMarkers
import { getColorByIndex } from 'src/mapbox-helpers/color-utils';
import MapboxFeature from 'src/mapbox-helpers/MapboxFeature';
import { bbox } from '@turf/turf';
import { roundToTwoDecimalPlaces } from 'src/util/util';
import { LocationAdminDetail, DeploymentAdminDetailBase, DeviceAdminDetail, DeviceType } from 'src/api/Api';
import { formatDate } from 'src/util/date-utils';

const MapComponent: React.FC = () => {
  const mapContainer = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<Map | null>(null);
  const { state, dispatch } = useAppState();
  const { bioMapData } = state.selectedProject;
  const { selectedSite, selectedLocation, mapOpacity } = state.bioInteractiveMapFilter;
  const [tooltip, setTooltip] = useState({
    show: false,
    x: 0,
    y: 0,
    content: '',
  });

  const locationMarkersRef = useRef<mapboxgl.Marker[]>([]);
  const popupRef = useRef<Popup | null>(null);

  // Use a ref to hold the latest filter state to avoid it being a dependency in useCallback
  const filterStateRef = useRef(state.bioInteractiveMapFilter);
  useEffect(() => {
    filterStateRef.current = state.bioInteractiveMapFilter;
  }, [state.bioInteractiveMapFilter]);

  const renderLocationMarkers = useCallback((map: Map, locations: LocationAdminDetail[], deployments: DeploymentAdminDetailBase[], devices: DeviceAdminDetail[]) => {
    locationMarkersRef.current.forEach(marker => marker.remove());
    locationMarkersRef.current = [];

    const deploymentsByLocation = deployments.reduce((acc, deployment) => {
      (acc[deployment.location_id] = acc[deployment.location_id] || []).push(deployment);
      return acc;
    }, {} as Record<string, DeploymentAdminDetailBase[]>);

    locations.forEach((location) => {
      if (!location.location) return;
      const point = MapboxFeature.parseFeatureFromString(location.location);
      if (point?.geometry?.type === 'Point') {
        const coordinates = point.geometry.coordinates as [number, number];
        if (coordinates && map) {
          const locDeployments = deploymentsByLocation[location.location_id] || [];
          const icon = 'communications-tower.svg';
          const markerEl = document.createElement('div');
          markerEl.className = 'location-marker';
          markerEl.setAttribute('data-location-id', location.location_id);
          markerEl.style.backgroundImage = `url(/maki_icons/svgs/${icon})`;
          markerEl.style.width = '30px';
          markerEl.style.height = '30px';
          markerEl.style.backgroundSize = 'contain';
          markerEl.style.cursor = 'pointer';

          const marker = new mapboxgl.Marker(markerEl)
            .setLngLat(coordinates)
            .addTo(map);

          locationMarkersRef.current.push(marker);

          marker.getElement().addEventListener('click', (e) => {
            e.stopPropagation();
            popupRef.current?.remove();

            const popupNode = document.createElement('div');
            const deploymentsList = locDeployments.map(dep => {
              const device = devices.find(d => d.device_id === dep.device_id);
              const depIcon = device?.type === DeviceType.CAMERA ? 'attraction.svg' : 'mobile-phone.svg';
              return (
                `<li data-deployment-id="${dep.deployment_id}" style="cursor: pointer; padding: 5px; list-style: none; display: flex; align-items: center; gap: 5px;">
                  <img src="/maki_icons/svgs/${depIcon}" width="20" height="20" />
                  <div>
                    <strong>${dep.deployment_name}</strong><br/>
                    <small>${formatDate(dep.start_date)} - ${formatDate(dep?.end_date)}</small>
                  </div>
                </li>`
              );
            }).join('');

            popupNode.innerHTML = `
              <h4>${location.location_name}</h4>
              <ul style="margin: 0; padding: 0;">${deploymentsList}</ul>
            `;

            popupNode.addEventListener('click', (ev) => {
              const target = ev.target as HTMLElement;
              const listItem = target.closest('li[data-deployment-id]');
              if (listItem) {
                const deploymentId = listItem.getAttribute('data-deployment-id');
                const deployment = locDeployments.find(d => d.deployment_id === deploymentId);
                const device = deployment ? devices.find(d => d.device_id === deployment.device_id) : undefined;
                dispatch({
                  type: 'SET_BIO_INTERACTIVE_MAP_FILTER',
                  payload: {
                    ...filterStateRef.current, // Use the ref to get latest state
                    selectedDeployment: deployment,
                    selectedDevice: device,
                  },
                });
                popupRef.current?.remove();
              }
            });

            popupRef.current = new mapboxgl.Popup({ closeOnClick: true, closeButton: true })
              .setLngLat(coordinates)
              .setDOMContent(popupNode)
              .addTo(map);
          });
        }
      }
    });
  }, [dispatch]); // Now this callback is stable

  const onMapLoad = useCallback(() => {
    if (!mapRef.current || !bioMapData) return;
    const map = mapRef.current;

    if (bioMapData.sites) {
      bioMapData.sites.forEach((site, index) => {
        addSiteBoundaryWithColor(map, site, getColorByIndex(index));
        const layerId = `fill-site-boundary-${site.site_id}`;
        map.on('mouseenter', layerId, (e) => {
          if (e.features && e.features.length > 0) {
            const properties = e.features[0].properties;
            const content = `
              <div>
                <strong>${properties?.Name}</strong><br/>
                ${roundToTwoDecimalPlaces(properties?.Area)} ha
              </div>
            `;
            setTooltip({
              show: true,
              x: e.point.x,
              y: e.point.y,
              content,
            });
          }
        });
        map.on('mouseleave', layerId, () => {
          setTooltip({ ...tooltip, show: false });
        });
      });
    }

    if (bioMapData.sites && bioMapData.sites.length > 0) {
      fitMapToBoundaries(map, bioMapData.sites);
    }
  }, [bioMapData]);

  useEffect(() => {
    if (mapRef.current || !mapContainer.current) return;

    mapRef.current = new mapboxgl.Map({
      container: mapContainer.current,
      style: 'mapbox://styles/mapbox/satellite-v9',
      center: [mapBoxConfig.defaulView.lng, mapBoxConfig.defaulView.lat],
      zoom: mapBoxConfig.defaulView.zoom,
    });

    mapRef.current.addControl(new mapboxgl.NavigationControl());

    mapRef.current.on('load', onMapLoad);

    return () => {
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
      }
    };
  }, [onMapLoad]);

  useEffect(() => {
    if (!mapRef.current) return;

    if (!selectedSite) {
      if (bioMapData?.sites) {
        fitMapToBoundaries(mapRef.current, bioMapData.sites);
      }
      return;
    };

    const siteToFocus = bioMapData?.sites?.find(s => s.site_id === selectedSite.site_id);

    if (siteToFocus?.boundary) {
      const tempFeature = new MapboxFeature();
      tempFeature.add(siteToFocus.boundary, {});
      const geoJsonBoundary = tempFeature.toJSON();

      if (geoJsonBoundary && geoJsonBoundary.features.length > 0) {
        try {
          const calculatedBbox = bbox(geoJsonBoundary);
          mapRef.current.fitBounds(calculatedBbox as [number, number, number, number], {
            padding: 50,
            animate: true,
            duration: 1500,
          });
        } catch (e) {
          console.error("Error calculating bounding box for single site:", e);
        }
      }
    }
  }, [selectedSite, bioMapData?.sites]);

  useEffect(() => {
    if (!mapRef.current) return;
    const map = mapRef.current;
    const locationLayerId = 'location-markers-circle';
    const locationLayer = map.getLayer(locationLayerId);

    // Remove the old circle layer if it exists
    if (locationLayer) {
      map.removeLayer(locationLayerId);
      map.removeSource(locationLayerId);
    }

    // Re-render custom markers when selectedSite or selectedLocation changes
    if (bioMapData) {
      renderLocationMarkers(map, bioMapData.locations, bioMapData.deployments, bioMapData.devices);
    }

    bioMapData?.sites?.forEach(site => {
      const layerId = `fill-site-boundary-${site.site_id}`;
      if (map.getLayer(layerId)) {
        const opacity = (selectedSite && selectedSite.site_id !== site.site_id) ? 0.2 : mapBoxConfig.polygon.defaultPolygonOpacity;
        map.setPaintProperty(layerId, 'fill-opacity', opacity);
      }
    });

  }, [selectedSite, selectedLocation, bioMapData?.locations, bioMapData?.sites, bioMapData?.deployments, bioMapData?.devices, renderLocationMarkers]);

  useEffect(() => {
    if (!mapRef.current) return;

    if (selectedLocation) {
      const locationToFocus = bioMapData?.locations?.find(l => l.location_id === selectedLocation.location_id);
      if (locationToFocus?.location) {
        try {
          const feature = MapboxFeature.parseFeatureFromString(locationToFocus.location, {});
          if (feature && feature.geometry.type === 'Point') {
            const coordinates = feature.geometry.coordinates as [number, number];
            mapRef.current.flyTo({
              center: coordinates,
              zoom: 17, // or a suitable zoom level
              essential: true,
            });
          }
        } catch (e) {
          console.error('Error parsing location', e);
        }
      }
    } else if (selectedSite) {
      const siteToFocus = bioMapData?.sites?.find(s => s.site_id === selectedSite.site_id);
      if (siteToFocus) {
        fitMapToBoundaries(mapRef.current, [siteToFocus]);
      }
    }
  }, [selectedLocation, bioMapData?.locations, selectedSite]);

  // New useEffect to handle opacity changes
  useEffect(() => {
    if (!mapRef.current || !bioMapData) return;
    const map = mapRef.current;

    // Update site layer opacities
    bioMapData.sites?.forEach(site => {
      const layerId = `fill-site-boundary-${site.site_id}`;
      if (map.getLayer(layerId)) {
        map.setPaintProperty(layerId, 'fill-opacity', mapOpacity);
      }
    });

  }, [mapOpacity, bioMapData?.sites]);

  const handleOpacityChange = (_event: Event, newValue: number | number[]) => {
    dispatch({
      type: 'SET_MAP_OPACITY',
      payload: newValue as number,
    });
  };

  return (
    <Box
      ref={mapContainer}
      sx={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
      }}
    >
      {tooltip.show && (
        <div
          style={{
            position: 'absolute',
            left: tooltip.x,
            top: tooltip.y,
            transform: 'translate(-50%, -100%)',
            backgroundColor: 'white',
            padding: '5px',
            borderRadius: '3px',
            boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
            zIndex: 1000,
            pointerEvents: 'none'
          }}
          dangerouslySetInnerHTML={{ __html: tooltip.content }}
        />
      )}
      <Box
        sx={{
          position: 'absolute',
          top: 10,
          right: 50,
          zIndex: 1000,
          backgroundColor: mapBoxConfig.mapUIElementBackgroundColor,
          padding: '10px',
          borderRadius: '5px',
          width: 200,
        }}
      >
        <Typography id="opacity-slider" gutterBottom>
          Layer Opacity
        </Typography>
        <Slider
          aria-labelledby="opacity-slider"
          value={mapOpacity}
          onChange={handleOpacityChange}
          min={0.1}
          max={1.0}
          step={0.1}
          valueLabelDisplay="auto"
        />
      </Box>
    </Box>
  );
};

export default MapComponent;